import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    model,
    numberAttribute,
    output,
    untracked,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25VirtualGridModule } from "../s25-virtual-grid/s25.virtual.grid.module";
import { S25VirtualGridComponent } from "../s25-virtual-grid/s25.virtual.grid.component";
import { Grid } from "../s25-virtual-grid/s25.virtual.grid";
import { GridUtil } from "../s25-virtual-grid/s25.virtual.grid.util";
import { Bind } from "../../decorators/bind.decorator";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AvailService, EventReqInfo } from "../../services/avail.service";
import { Item } from "../../pojo/Item";
import { S25Util } from "../../util/s25-util";
import { AvailWeekly } from "./s25.avail.weekly.util";
import { S25ItemModule } from "../s25-item/s25.item.module";
import { S25OfficeHoursSliderModule } from "../s25-office-hours-slider/s25.office.hours.slider.module";
import { DaysOfWeekPref, DaysOfWeekPrefDay, PreferenceService } from "../../services/preference.service";
import { DecimalPipe, LowerCasePipe } from "@angular/common";
import { ClearMemo, Memo } from "../../decorators/memo.decorator";
import { SpaceService } from "../../services/space.service";
import { S25DatetimePrefssUtil } from "../s25-datetime-prefs/s25.datetime.prefs.util";
import { S25AvailWeeklyOptionsComponent } from "./s25.avail.weekly.options.component";
import { ItemColorMappingService } from "../../services/item.color.mapping.service";
import { S25AvailLegendComponent } from "./s25.avail.legend.component";
import { S25ModalModule } from "../s25-modal/s25.modal.module";
import { WaitlistStore } from "../../stores/waitlist.store";
import { WaitlistEntry, WaitingEntry } from "../../pojo/Waitlist";
import { EventData } from "../../services/event.service";
import { EventReservation } from "../../services/reservation.service";
import { AvailUtil } from "../s25-availability/avail.util";
import { Task } from "../../pojo/Task";
import SpaceCompete = Task.SpaceCompete;
import { TaskCompetingUtil } from "../s25-task/task.competing.util";
import { s25Resource } from "../../util/s25.resource";
import { UserStore } from "../../stores/user.store";
import { TypedTemplateContextDirective, VoidTemplateDirective } from "../../standalone/typed.template.directive";
import { S25LoadingInlineStaticComponent } from "../s25-loading/s25.loading.inline.static.component";

@TypeManagerDecorator("s25-ng-avail-weekly")
@Component({
    selector: "s25-ng-avail-weekly",
    template: `
        @if (!data.lastValue()) {
            <s25-ng-loading-inline-static />
        } @else if (data.error()) {
            Something went wrong
        } @else {
            <s25-ng-virtual-grid
                #virtualGrid
                [items]="items()"
                [columnHeaders]="columnHeaders()"
                [rowHeaders]="rowHeaders()"
                [interactionHandlers]="interactionHandlers"
                [columnHeaderTemplate]="columnHeader"
                [rowHeaderTemplate]="rowHeader"
                [createTemplate]="createTemplate"
                [cornerTemplate]="corner"
                [optionsBelowTemplate]="options"
                [itemTemplate]="item"
                [itemProjector]="itemProjector"
                [domainEquals]="domainEquals"
                [canCreate]="{ x: true, y: true }"
                [isLoading]="data.isLoading()"
                [style.--max-tracks]="maxTracks()"
            />
        }

        <ng-template #corner void>
            <s25-ng-office-hours-slider [start]="startHour()" [end]="endHour()" (onChange)="onHoursChange($event)" />
        </ng-template>

        <ng-template #options void>
            @if (!noOptions()) {
                <s25-ng-avail-weekly-options
                    [(includeRequested)]="includeRequested"
                    [firstDate]="firstDate()"
                    (firstDateChange)="firstDateInput.set($event)"
                    [(weeks)]="weeks"
                    (daysChange)="dayChooserChange($event)"
                    [(utilizationMode)]="utilizationMode"
                    [mode]="mode()"
                    (modeChange)="modeInput.set($event)"
                    (legend)="legend.open()"
                    [showManageWaitlistButton]="true"
                    [waitlistEvents]="requestTypeData()"
                    [itemIds]="[itemId()]"
                    (refresh)="refresh()"
                    [hasHelp]="hasHelp()"
                    [isLoading]="data.isLoading()"
                />
            }
        </ng-template>

        <s25-ng-modal #legend [title]="'Availability Color Legend'" [size]="'xs'">
            <ng-template #s25ModalBody void>
                <s25-ng-avail-legend />
            </ng-template>
        </s25-ng-modal>

        <ng-template #columnHeader [templateContext]="columnHeaderTemplateContext" let-header="header">
            <div class="column-header {{ header.availWeekly.type }}" [class.today]="header.availWeekly.isToday">
                {{ header.heading }}
            </div>
        </ng-template>

        <ng-template #rowHeader [templateContext]="rowHeaderTemplateContext" let-header="header">
            <div class="row-header" [class.afternoon]="header.availWeekly.hour >= NOON">
                {{ header.heading }}
            </div>
        </ng-template>

        <ng-template #item [templateContext]="itemTemplateContext" let-item="item">
            @let utilization = utilizationMode();
            <div
                class="item grid-color"
                [attr.data-type]="item.availWeekly.type | lowercase"
                [attr.data-event-type]="item.availWeekly.eventType"
                [attr.data-state]="item.availWeekly.eventState"
                [style.--pre-percent.%]="item.availWeekly.prePercent"
                [style.--event-percent.%]="item.availWeekly.eventPercent"
                [style.--post-percent.%]="item.availWeekly.postPercent"
                [attr.data-utilization]="hasUtilization(item, utilization)"
                [style.--utilization.%]="
                    hasUtilization(item, utilization) ? item.availWeekly.utilization[utilization] * 100 : undefined
                "
            >
                @if (item.availWeekly.isEvent) {
                    <s25-item
                        [itemId]="item.availWeekly.id"
                        [itemTypeId]="Item.Ids.Event"
                        [itemName]="item.availWeekly.name"
                        [itemId2]="item.availWeekly.reservationId"
                        [hideLabel]="true"
                        [tooltipService]="AvailWeekly.eventTooltipService"
                        [tooltipContext]="item.availWeekly"
                    >
                        <div class="eventWrapper">
                            @if (!!item.availWeekly.prePercent) {
                                <div class="pre"></div>
                            }
                            <div class="event">
                                <span class="name">
                                    @if (hasUtilization(item, utilization)) {
                                        {{ item.availWeekly.utilization[utilization] * 100 | number: "1.0-0" }}%
                                    }
                                    {{ item.availWeekly.name }}
                                </span>
                            </div>
                            @if (!!item.availWeekly.postPercent) {
                                <div class="post"></div>
                            }
                        </div>
                        @if (
                            item.availWeekly?.type === "Requested" &&
                            item.availWeekly.waitlistView?.isWaitlist &&
                            waitlistEnabled.value()
                        ) {
                            <div class="wl-section">
                                <span class="truncate-text">{{ item.availWeekly.name }}</span>

                                <s25-ng-icon
                                    [type]="'queue'"
                                    [ariaLabel]="item.availWeekly.waitlistView?.queueLabel ?? ''"
                                >
                                </s25-ng-icon>
                            </div>
                        }
                    </s25-item>
                } @else {
                    <span class="name">{{ item.availWeekly.name }}</span>
                }
            </div>
        </ng-template>

        <ng-template #createTemplate void>
            <button class="create-item aw-button aw-button--none">
                <s25-ng-icon [type]="'pen'" [ariaLabel]="'Create event'" />
            </button>
        </ng-template>
    `,
    styles: `
        :host {
            --column-header-bg: #fff0eb;
            --column-header-today-bg: #f1c7b9;

            --row-header-blue-even: #88a7dd;
            --row-header-blue-odd: #94b5ef;
            --row-header-border-color-afternoon: #888;

            --event-border-color: black;
            --create-background: beige;
            --event-no-utilization-background: #d4d4d4;
            --event-no-utilization-color: black;

            --event-border: 1px solid var(--event-border-color);
            --item-overlap: 1px;

            /* Variables for the virtual grid */
            --grid-column-line-color: black;
            --grid-outline-border-width: 1px;
            --grid-outline-border-style: solid;
            --grid-outline-border-color: black;
            --grid-column-width-first: clamp(75px, 10cqi, 150px);
            --grid-column-width-min: 40px;
            --grid-column-width-max: 250px;
        }

        ::ng-deep .nm-party--on s25-ng-avail-weekly {
            --column-header-bg: #3c3d46;
            --column-header-today-bg: #2d2d34;

            --row-header-blue-even: #277abe;
            --row-header-blue-odd: #23659c;
            --row-header-border-color-afternoon: #888;

            --event-border-color: black;
            --create-background: beige;
            --event-no-utilization-background: #d4d4d4;
            --event-no-utilization-color: black;

            /* Variables for the virtual grid */
            --grid-column-line-color: var(--border-color);
            --grid-outline-border: 1px solid var(--border-color);
        }

        s25-ng-loading-inline-static {
            text-align: center;
        }

        s25-ng-virtual-grid {
            --grid-column-step: calc(1px * var(--max-tracks));
        }

        .column-header {
            background-color: var(--column-header-bg);
        }

        .column-header.dow {
            font-size: min(1em, 8cqi + 0.5em);
            font-weight: bold;
            overflow: hidden;
        }

        .column-header.today {
            --column-header-bg: var(--column-header-today-bg);
        }

        .row-header {
            padding-inline: 0.5em;
            text-align: center;
            text-transform: uppercase;
        }

        .row-header.afternoon {
            background-color: var(--row-header-blue-odd);
        }

        .item {
            height: 100%;
            border: var(--event-border);
            text-align: center;
            overflow: hidden;
            font-size: min(1em, 8cqi + 0.5em);
            line-height: calc(var(--row-height) - 1px);
            user-select: text;
            position: relative;
        }

        .item:not([data-type="blackout"]):not([data-type="closed"]) {
            z-index: 1;
        }

        .item:where([data-type="pending"], [data-type="requested"], [data-type="draft"]) .name {
            background-color: var(--grid-color--background-color);
            padding: 0.1em;
            border-radius: 3px;
        }

        :host:not([data-utilization-mode="none"]) .item[data-utilization="false"]:not([data-type="blackout"]) {
            --grid-color--background-color: var(--event-no-utilization-background);
            --grid-color--color: var(--event-no-utilization-color);
        }

        .eventWrapper {
            flex: 1 1 0;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }

        .pre,
        .post {
            background-color: rgb(255 255 255 / 40%);
        }

        .pre {
            flex-basis: var(--pre-percent);
        }

        .post {
            flex-basis: var(--post-percent);
        }

        .pre + .event,
        .event + .post {
            border-top: 1px dotted black;
        }

        .event {
            text-overflow: ellipsis;
            overflow: hidden;
            overflow-wrap: anywhere;
            text-wrap: wrap;
            padding-inline: 0.2em;
            flex-basis: var(--event-percent);
        }

        .create-item {
            height: 100%;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            text-align: center;
            overflow: hidden;
            font-size: min(1em, 8cqi + 0.5em);
            line-height: calc(var(--row-height) - 1px);
            user-select: text;

            s25-ng-icon {
                width: 11px; /* Center on the paper of the pen icon */
            }
        }

        :host ::ng-deep .grid--column-line:not([data-truncate-boundary]),
        :host ::ng-deep.grid--column-headers--sub-headers .leaf:not(:first-child) {
            --column-line-width: 1px;
            --column-line-color: var(--border-color);
        }

        :host ::ng-deep.grid--column-header.leaf {
            border-top-color: var(--column-header-bg) !important;
        }

        :host ::ng-deep .grid--column-header.leaf:has(.today) {
            border-top-color: var(--column-header-today-bg) !important;
        }

        :host ::ng-deep .grid--row-header.leaf:has(.row-header.afternoon):not(:last-child) {
            border-bottom-color: var(--row-header-border-color-afternoon);
        }

        :host ::ng-deep .grid--row-header.leaf:nth-child(odd) .row-header.afternoon {
            background-color: var(--row-header-blue-even);
        }

        :host ::ng-deep .grid--item {
            /* Make items on the right (left+width=100%) wider to prevent double border */
            --width-overlap: max(
                var(--item-left) + var(--item-width) - 100% + var(--item-overlap) * 2,
                var(--item-overlap)
            );
            left: calc(var(--item-left) - var(--item-overlap)) !important;
            width: calc(var(--item-width) + var(--width-overlap)) !important;
            top: calc(var(--item-top) - var(--item-overlap)) !important;
            height: calc(var(--item-height) + var(--item-overlap)) !important;
        }

        :host ::ng-deep .grid--create-item {
            transform: translate3d(
                calc(var(--item-left) - var(--item-overlap)),
                calc(var(--item-top) - var(--item-overlap)),
                0
            ) !important;
            width: calc(var(--item-width) + var(--item-overlap)) !important;
            height: calc(var(--item-height) + var(--item-overlap)) !important;
        }

        :host ::ng-deep .grid--area.creating .create-item {
            border: var(--event-border);
            background-color: var(--create-background);
        }

        :host ::ng-deep s25-item {
            cursor: pointer;
        }

        :host ::ng-deep .s25-item--event,
        :host ::ng-deep .s25-item--event .ngInlineBlock,
        :host ::ng-deep .s25-item--event .ngInlineBlock > div {
            display: block;
            height: 100%;
            line-height: inherit;
        }

        :host ::ng-deep .s25-item--event .s25-item-holder {
            display: flex;
            flex-direction: column;
            height: 100%;
            min-height: 0;
            line-height: inherit;
        }

        .wl-section {
            display: flex;
            align-items: center;
            gap: 0.25rem; /* Optional spacing between text and icon */
            flex-shrink: 0;
        }

        .wl-section .icon {
            display: inline-flex;
            align-items: center;
        }

        .truncate-text {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1 1 auto; /* let it shrink if needed */
            min-width: 0; /* IMPORTANT to allow truncation in flex */
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    host: { "[attr.data-utilization-mode]": "utilizationMode()" },
    imports: [
        S25VirtualGridModule,
        S25OfficeHoursSliderModule,
        S25AvailWeeklyOptionsComponent,
        LowerCasePipe,
        S25ItemModule,
        S25IconComponent,
        DecimalPipe,
        S25AvailLegendComponent,
        S25ModalModule,
        VoidTemplateDirective,
        TypedTemplateContextDirective,
        S25LoadingInlineStaticComponent,
    ],
    standalone: true,
})
export class S25AvailWeeklyComponent {
    readonly itemType = input.required<Item.Id>();
    readonly itemId = input.required({ transform: numberAttribute });
    readonly firstDateInput = model<Date | number | string>(new Date(), { alias: "firstDate" });
    readonly noOptions = input(false);
    readonly includeRequested = model(false);
    readonly weeks = model(1);
    readonly dowsInput = input<Set<AvailWeekly.Dow> | undefined>(undefined, { alias: "dows" });
    readonly modeInput = model<AvailWeekly.Mode | undefined>(undefined, { alias: "mode" });
    readonly startHourInput = model<number | undefined>(undefined, { alias: "startHour" });
    readonly endHourInput = model<number | undefined>(undefined, { alias: "endHour" });
    readonly hasHelp = input(true);
    readonly utilizationMode = model<AvailWeekly.UtilizationMode>("none");

    readonly requestTypeDataWeekly = output<EventReqInfo[]>();

    private readonly virtualGridRef =
        viewChild<S25VirtualGridComponent<AvailWeekly.ColumnHeader, AvailWeekly.RowHeader, AvailWeekly.Item>>(
            "virtualGrid",
        );
    get virtualGrid() {
        return this.virtualGridRef();
    }

    private readonly waitlistStore = inject(WaitlistStore);
    readonly userStore = inject(UserStore);

    protected readonly itemTemplateContext: Grid.ItemProjectionTemplateContext<AvailWeekly.Item, {}>;
    protected readonly domainEquals = GridUtil.positionsEqual;
    protected readonly itemProjector = (item: AvailWeekly.Item): readonly Grid.ItemProjectionDefinition[] => [
        {
            key: item.id,
            role: "primary",
            data: {},
            position: item.position,
            ariaLabel: computed(() => item.ariaLabel?.() ?? item.id),
            hidden: computed(() => item.hidden?.() ?? false),
            noInteraction: computed(() => item.noInteraction?.() ?? false),
            minimapColor: computed(() => item.minimapColor?.()),
        },
    ];
    protected readonly columnHeaderTemplateContext: Grid.HeaderTemplateContext<AvailWeekly.ColumnHeader>;
    protected readonly rowHeaderTemplateContext: Grid.HeaderTemplateContext<AvailWeekly.RowHeader>;

    readonly startHour = computed(() => this.startHourInput() ?? this.officeHours.value()?.start ?? 0);
    readonly endHour = computed(() => this.endHourInput() ?? this.officeHours.value()?.end ?? 23);
    readonly dows = linkedSignal(() => {
        const dowsInput = this.dowsInput();
        if (dowsInput?.size ?? 0 > 0) return dowsInput;
        return AvailWeekly.getDowsFromPref((this.days.value() ?? []) as DaysOfWeekPrefDay[]);
    });
    readonly mode = computed(() => this.modeInput() ?? (this.separated.value() ? "separated" : "overlapping"));

    readonly officeHours = s25Resource({
        if: () => this.startHourInput() === undefined || this.endHourInput() === undefined,
        loader: () => PreferenceService.getOfficeHours(),
    });
    readonly eventDisplay = s25Resource(() => PreferenceService.getEventDisplay());
    readonly waitlistNeeded = computed(() => this.itemType() === Item.Ids.Location && this.includeRequested());
    readonly waitlistEnabled = s25Resource({
        params: () => this.itemId(),
        loader: ({ params: itemId }) => TaskCompetingUtil.waitlistEnabled(itemId),
    });
    readonly getEnabledBucketType = s25Resource(() => ItemColorMappingService.getEnabledBucketType());
    readonly days = s25Resource(() => PreferenceService.getDays());
    readonly perms = s25Resource({
        params: () => this.itemId(),
        loader: async ({ params: itemId }) => {
            const page = await SpaceService.getSpaceAssignPermsPage(`&space_id=${itemId}`);
            return page?.items?.get(itemId);
        },
    });
    readonly instanceTZ = s25Resource(async () => {
        const prefs = await PreferenceService.getPreferences(["timezone"], "S");
        return S25DatetimePrefssUtil.timeZoneToIANA[prefs?.timezone?.value || 25];
    });
    readonly space = s25Resource({
        params: () => this.itemId(),
        loader: ({ params: itemId }) => SpaceService.getSpaceMinimal(itemId),
    });
    readonly colorMap = s25Resource(() => ItemColorMappingService.getEnabledBucketType());
    readonly separated = s25Resource({
        if: () => !this.modeInput(),
        loader: () => AvailService.getSeparatedPref(),
    });
    readonly data = s25Resource({
        if: () =>
            this.eventDisplay.value() !== undefined &&
            this.space.value() !== undefined &&
            (this.startHourInput() !== undefined ||
                this.endHourInput() !== undefined ||
                this.officeHours.value() !== undefined),
        params: () => ({
            itemId: this.itemId(),
            itemType: this.itemType(),
            weeks: this.weeks(),
            includeRequested: this.includeRequested(),
            eventDisplay: this.eventDisplay.value()!,
            weekStart: this.userStore.weekstart(),
            startHour: this.startHour(),
            endHour: this.endHour(),
            firstDate: this.firstDate(),
            space: this.space.value(),
            waitlistNeeded: this.waitlistNeeded(),
        }),
        loader: async ({ params, abortSignal }) => {
            const availability = await AvailService.getWeeklyAvailability(
                {
                    displayEndTime: params.endHour,
                    displayStartTime: params.startHour,
                    isItemNameTitle: params.eventDisplay === "title",
                    itemId: Number(params.itemId),
                    itemType: params.itemType,
                    numWeeks: params.weeks,
                    startDt: params.firstDate,
                    includeRequested: params.includeRequested,
                    weekStart: params.weekStart,
                },
                { abortSignal },
            );
            if (abortSignal.aborted) return;
            const existingRsrvIds = new Set(availability.map((item) => item.itemId2));

            let waitlistEntries: WaitlistEntry[] = [];
            let waitlistItems: ReturnType<typeof this.mapWaitlistEntryToAvailItem>[] = [];
            if (params.waitlistNeeded) {
                const endDt = S25Util.date.toEndOfDay(
                    S25Util.date.lastDayOfWeek(S25Util.date.addWeeks(params.firstDate, params.weeks), params.weekStart),
                );
                waitlistEntries = await this.waitlistStore.getWaitlistEntries(
                    {
                        space_id: params.itemId,
                        start_dt: params.firstDate,
                        end_dt: endDt,
                        include_requests: "all_competes",
                    },
                    true,
                );

                const [eventData, reservationData] = await Promise.all([
                    AvailUtil.enrichWaitlistEntries(waitlistEntries),
                    AvailUtil.fetchReservationMap(waitlistEntries, existingRsrvIds),
                ]);

                waitlistItems = waitlistEntries
                    .flatMap((entry) =>
                        (entry.waiting ?? [])
                            .filter((waiting) => !waiting.notifyId && !existingRsrvIds.has(waiting.rsrvId))
                            .map((waiting) => this.mapWaitlistEntryToAvailItem(waiting, eventData, reservationData)),
                    )
                    .filter((row): row is NonNullable<typeof row> => row !== null);
            }

            return { params, availability, waitlistEntries, waitlistItems };
        },
    });

    readonly dowArr = computed(() => {
        const dows = this.dows();
        if (!dows) return [];
        return AvailWeekly.getDowArr({ dows, weekStart: this.userStore.weekstart() });
    });

    readonly items = computed(() => this.getItems());
    protected readonly columnHeaders = computed(() => {
        const data = this.data.lastValue();
        if (!data) return [];

        return AvailWeekly.getColumns({
            weekStart: data.params.weekStart,
            firstDate: data.params.firstDate,
            weeks: data.params.weeks,
            dows: this.dowArr(),
        });
    });
    protected readonly rowHeaders = computed(() => {
        return AvailWeekly.getRows({
            startHour: this.startHour(),
            endHour: this.endHour(),
            is24: this.userStore.is24Hours(),
        });
    });
    protected readonly maxTracks = computed(() => {
        if (this.mode() !== "separated") return 1;
        const max = S25Util.array.max(this.items(), (item) => item.availWeekly.track ?? 1);
        return Math.max(max, 1);
    });

    readonly requestTypeData = computed(() => this.getRequestTypeData());
    protected readonly firstDate = computed(
        () => S25Util.date.firstDayOfWeekFromDate(new Date(this.firstDateInput()), this.userStore.weekstart()),
        { equal: S25Util.date.equal },
    );

    readonly interactionHandlers: Grid.InteractionHandlers<AvailWeekly.Item> = {
        onCreateShow: this.canCreateAt,
        onCreateDrag: this.canCreateAt,
        onCreate: this.onCreate,
    };

    protected readonly LAST_HOUR_OF_DAY = 23;
    protected readonly NOON = 12;

    constructor() {
        effect((onCleanup) => {
            const colorMap = this.colorMap.value();
            if (!colorMap) return;
            const styleElement = document.createElement("style");
            styleElement.dataset.s25ItemColorMapping = "";
            styleElement.textContent = ItemColorMappingService.compileCss(colorMap);
            document.head.appendChild(styleElement);
            onCleanup(() => styleElement.remove());
        });
        effect(() => {
            const requestTypeData = this.requestTypeData();
            if (!requestTypeData.length) return;
            this.requestTypeDataWeekly.emit(requestTypeData);
        });
    }

    @Bind
    @ClearMemo("canCreateAt")
    private getItems(): AvailWeekly.Item[] {
        const data = this.data.lastValue();
        const space = this.space.lastValue();
        if (!data || !space) return [];

        const { params, availability, waitlistItems, waitlistEntries } = data;
        const items = AvailWeekly.initializeItems({
            items: [
                ...availability,
                ...(<Awaited<ReturnType<typeof AvailService.getWeeklyAvailability>>>waitlistItems),
            ],
            dows: this.dowArr(),
            weeks: params.weeks,
            firstDate: params.firstDate,
            maxCapacity: space.max_capacity || 0,
            colorMap: (this.colorMap.value() ?? this.getEnabledBucketType.value())!,
            is24: this.userStore.is24Hours(),
        });

        untracked(() => AvailWeekly.separateOverlappingItems(items, this.mode));

        return this.waitlistInitializeItems(items, waitlistEntries);
    }

    @Bind
    @Memo({
        name: "canCreateAt",
        hasher: (createData: Grid.Rect) => [createData.left, createData.top, createData.height, createData.width],
        scopeToContext: true,
    })
    async canCreateAt(createData: Grid.Rect) {
        const perms = this.perms.value();
        if (!perms) return false;
        if (perms.assignOverride) return true;

        const headers = this.virtualGrid?.positionToHeaders(createData);
        if (!headers) return false;
        const event = AvailWeekly.headersToEvent(headers);

        return (event.occurrences || [event]).every((occ: { startDt: Date; endDt: Date }) => {
            return this.hasCreatePermsAt(occ.startDt, occ.endDt);
        });
    }

    hasCreatePermsAt(start: Date, end: Date) {
        const perms = this.perms.value();
        const instanceTZ = this.instanceTZ.value();
        if (!perms || !instanceTZ) return false;

        const userTZ = this.userStore.timezone();
        return AvailService.hasEventPerms(
            "create",
            perms,
            S25Util.date.timezone.convertTo(start, userTZ),
            S25Util.date.timezone.convertTo(end, userTZ),
            { instance: instanceTZ, user: userTZ },
        );
    }

    @Bind
    async onCreate(createData: Grid.Rect) {
        const headers = this.virtualGrid?.positionToHeaders(createData);
        if (!headers) return;
        const event = AvailWeekly.headersToEvent(headers);
        event.itemId = this.itemId();

        await AvailService.createEvent(event, this.itemType());
    }

    setDowsFromPref(days: DaysOfWeekPrefDay[]) {
        this.dows.set(AvailWeekly.getDowsFromPref(days));
    }

    dayChooserChange(days: DaysOfWeekPref) {
        this.setDowsFromPref(days.days.day);
    }

    onHoursChange(hours: { start: number; end: number }) {
        this.startHourInput.set(hours.start);
        this.endHourInput.set(hours.end);
    }

    refresh() {
        const reloads: Promise<unknown>[] = [this.data.reloadAndAwait()];
        return Promise.all(reloads);
    }

    hasUtilization(
        item: AvailWeekly.Item,
        utilizationMode: AvailWeekly.UtilizationMode,
    ): utilizationMode is keyof AvailWeekly.ItemData["utilization"] {
        if (utilizationMode === "none") return false;
        if (item.availWeekly!.type === "Related") return false;
        return isFinite(item.availWeekly!.utilization[utilizationMode]);
    }

    private waitlistInitializeItems(items: AvailWeekly.Item[], waitlistEntries: WaitlistEntry[]) {
        const requested = items.filter((item) => item.availWeekly.type === "Requested");
        for (const currentItem of requested) {
            const data = currentItem.availWeekly;
            const reservationId = data.reservationId;
            const currentStart = new Date(data.dates.setup);
            const currentEnd = new Date(data.dates.takedown);

            // Compete against any calendar reservation that uses setup→takedown on this grid (Event,
            // Requested, Related, Draft — see AvailWeekly.eventRsrvTypes / ItemData.isEvent), not only
            // other Requested rows. Otherwise a request overlapping a confirmed event never shows as waitlisted.
            const timeOverlappingReservations = items.filter((item) => {
                const itemData = item.availWeekly;
                if (!itemData?.isEvent) return false;

                const start = new Date(itemData.dates.setup);
                const end = new Date(itemData.dates.takedown);

                return this.hasConflict(currentStart, currentEnd, start, end);
            });

            const seenReservationIds = new Set<number>();
            const uniqueReservations = timeOverlappingReservations.filter((item) => {
                const id = item.availWeekly?.reservationId;
                if (id === undefined || seenReservationIds.has(id)) return false;
                seenReservationIds.add(id);
                return true;
            });

            const sortedReservations = [...uniqueReservations].sort(
                (left, right) => (left.availWeekly?.reservationId ?? 0) - (right.availWeekly?.reservationId ?? 0),
            );

            const reservationIds = sortedReservations.map((item) => item.availWeekly?.reservationId);

            const waitlistIndex = reservationId !== undefined ? reservationIds.indexOf(reservationId) : -1;

            const waitingPosition = waitlistEntries
                .flatMap((entry) => entry.waiting ?? [])
                .find((waiting) => Number(waiting.rsrvId) === Number(reservationId))?.position;

            // Grid overlap order (by rsrvId) drives the queue icon; API position is only a fallback when
            // this reservation is not in the overlapping set (otherwise every waiting row can show 1/N).
            const queueWaitlistOrderRaw = waitlistIndex >= 0 ? waitlistIndex + 1 : waitingPosition;
            const queueWaitlistOrder =
                queueWaitlistOrderRaw != null && Number.isFinite(Number(queueWaitlistOrderRaw))
                    ? Number(queueWaitlistOrderRaw)
                    : undefined;

            const competingRsrvCount = sortedReservations.length;

            const queueLabel = queueWaitlistOrder ? `${queueWaitlistOrder}/${competingRsrvCount}` : "";

            data.waitlistView = {
                isWaitlist: sortedReservations.length > 1,
                queueLabel,
                sortedReservations,
                queueOrder: queueWaitlistOrder,
            };
        }

        return items;
    }

    private getRequestTypeData() {
        const competing = this.items().filter((item) => {
            const data = item.availWeekly;
            if (!S25Util.isDefined(data.reservationId)) return false;
            if (!data.waitlistView?.isWaitlist) return false;
            return (data.waitlistView.queueOrder ?? 0) > 0;
        });
        const unique = S25Util.array.unique(competing, (item) => item.availWeekly.reservationId);

        const requestTypeData: EventReqInfo[] = [];
        for (const currentItem of unique) {
            const mappedCompetesData = currentItem.availWeekly.waitlistView?.sortedReservations
                .filter((item) => item.availWeekly?.reservationId !== currentItem.availWeekly?.reservationId)
                .map((item): SpaceCompete => {
                    const rowData = item.availWeekly!;
                    return {
                        spaceId: this.itemId(),
                        conflictType: "req_rsrv",
                        conflictId: rowData.id,
                        conflictRsrvId: rowData.reservationId!,
                        conflictName: rowData.name,
                        conflictRsrvStartDt: rowData.dates.setup,
                        conflictEvStartDt: rowData.dates.start,
                        conflictEvEndDt: rowData.dates.end,
                        conflictRsrvEndDt: rowData.dates.takedown,
                        conflictPreEventDt: rowData.dates.pre,
                        conflictPostEventDt: rowData.dates.post,
                    };
                })
                .filter(
                    (row, index, self) =>
                        index === self.findIndex((other) => other.conflictRsrvId === row.conflictRsrvId),
                );

            requestTypeData.push({
                itemId: currentItem.availWeekly.id,
                itemName: currentItem.availWeekly.name,
                rsrvId: currentItem.availWeekly.reservationId!,
                eventDate: currentItem.availWeekly.dates.start,
                objectId: this.itemId(),
                waitlistOrder: currentItem.availWeekly.waitlistView?.queueOrder,
                competingRsrvCount: currentItem.availWeekly.waitlistView?.sortedReservations.length,
                dates: currentItem.availWeekly.dates,
                competes: { spaceCompetes: (mappedCompetesData ?? []) as Task.SpaceCompete[] },
            });
        }

        return requestTypeData;
    }

    //requested overlapped
    hasConflict(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean {
        return aStart < bEnd && bStart < aEnd;
    }
    private mapWaitlistEntryToAvailItem(
        waiting: WaitingEntry,
        eventDataMap?: Map<number, EventData>,
        reservationMap?: Map<number, EventReservation>,
    ) {
        const rsrv = reservationMap?.get(waiting.rsrvId);
        const startDt = rsrv ? new Date(S25Util.date.dropTZString(rsrv.event_start_dt)) : new Date(waiting.startDt);
        const endDt = rsrv ? new Date(S25Util.date.dropTZString(rsrv.event_end_dt)) : new Date(waiting.endDt);

        if (isNaN(startDt.getTime()) || isNaN(endDt.getTime()) || endDt <= startDt) return null;

        const startDtStr = S25Util.date.toS25ISODateTimeStr(startDt);
        const endDtStr = S25Util.date.toS25ISODateTimeStr(endDt);

        if (!startDtStr || !endDtStr) return null;

        const eventId = waiting.eventId ?? 0;
        const eventData = eventId > 0 ? eventDataMap?.get(eventId) : undefined;

        return {
            hasPerm: eventData?.event_locator,
            event_type_id: eventData?.event_type_id,
            cur_event_state: eventData?.state,
            itemTypeId: 1,
            itemId: eventId,
            itemId2: waiting.rsrvId,
            rsrvType: 7,
            prePerc: 0,
            evPerc: 100,
            postPerc: 0,
            itemName: waiting.eventName || eventData?.event_name,
            start: S25Util.date.dateToHours(startDt),
            end: S25Util.date.dateToHours(endDt),
            dow: S25Util.date.getDayOfWeek(startDt),
            date: S25Util.date.getDate(startDt),
            expectedHeadcount: 0,
            registeredHeadcount: 0,
            setup_dt: startDtStr,
            pre_dt: startDtStr,
            start_dt: startDtStr,
            end_dt: endDtStr,
            post_dt: endDtStr,
            takedown_dt: endDtStr,
            waitlistPosition: waiting.position,
        };
    }

    protected readonly AvailWeekly = AvailWeekly;
    protected readonly Item = Item;
}
